import {
  CalendarClock,
  LogOut,
  Moon,
  Plug,
  Settings,
  ShieldCheck,
  Sparkles,
  Sun,
  User as UserIcon,
  Users,
} from 'lucide-react';
import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MyRoleSummary } from '@/features/employees/components/MyRoleSummary';
import { useEmployeeDirectoryEntry } from '@/features/employees/hooks';
import { usePermissions } from '@/hooks/usePermissions';
import { useSupabase } from '@/hooks/useSupabase';
import { supabase } from '@/lib/supabase';
import { ROLE_LABELS, type AppRole } from '@/types/app-roles';

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase() || '·';
}

export function UserMenu() {
  const { user } = useSupabase();
  const { roles, isSuperadmin, has } = usePermissions();
  const { data: dir } = useEmployeeDirectoryEntry(user?.id);
  const { resolvedTheme, setTheme } = useTheme();
  const navigate = useNavigate();

  const [mounted, setMounted] = useState(false);
  // Patrón next-themes: el primer paint no conoce el theme persistido.
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => setMounted(true), []);

  async function signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast.error('Error al cerrar sesión', { description: error.message });
      return;
    }
    navigate('/login', { replace: true });
  }

  const email = user?.email ?? '';
  const displayName =
    (user?.user_metadata?.display_name as string | undefined)?.trim() ||
    dir?.display_name?.trim() ||
    (email ? email.split('@')[0] : 'Sin sesión');
  const avatarUrl = dir?.avatar_url ?? null;

  const primaryRoleKey = isSuperadmin ? 'superadmin' : roles[0];
  const primaryRole = primaryRoleKey
    ? (ROLE_LABELS[primaryRoleKey as AppRole] ?? primaryRoleKey)
    : 'Sin rol';

  const isDark = resolvedTheme === 'dark';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="rounded-full"
          aria-label="Abrir menú de usuario"
        >
          <Avatar className="h-7 w-7">
            {avatarUrl && <AvatarImage src={avatarUrl} alt={displayName} />}
            <AvatarFallback className="bg-secondary text-secondary-foreground text-caption">
              {initialsFromName(displayName)}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="font-normal">
          <div className="flex items-center gap-2.5">
            <Avatar className="h-9 w-9">
              {avatarUrl && <AvatarImage src={avatarUrl} alt={displayName} />}
              <AvatarFallback className="bg-secondary text-secondary-foreground text-caption">
                {initialsFromName(displayName)}
              </AvatarFallback>
            </Avatar>
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-body-lg font-medium">{displayName}</span>
              {email && (
                <span className="text-muted-foreground truncate text-caption">
                  {email}
                </span>
              )}
              <span className="text-muted-foreground truncate text-caption">
                {primaryRole}
              </span>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="px-2 py-1.5">
          <MyRoleSummary compact />
        </div>
        <DropdownMenuSeparator />

        <DropdownMenuItem onSelect={() => navigate('/tareas')}>
          <CalendarClock className="mr-2 h-4 w-4" />
          Qué hay hoy
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {has('employees.view') && (
          <DropdownMenuItem onSelect={() => navigate('/equipo')}>
            <Users className="mr-2 h-4 w-4" />
            Directorio del equipo
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={() => navigate('/settings/profile')}>
          <UserIcon className="mr-2 h-4 w-4" />
          Perfil
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => navigate('/settings/mi-ia')}>
          <Sparkles className="mr-2 h-4 w-4" />
          IA &amp; Voz
        </DropdownMenuItem>
        {has('campaigns.write.own') && (
          <DropdownMenuItem onSelect={() => navigate('/connections')}>
            <Plug className="mr-2 h-4 w-4" />
            Conexiones
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={() => navigate('/settings/security')}>
          <ShieldCheck className="mr-2 h-4 w-4" />
          Seguridad · 2FA
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => navigate('/settings')}>
          <Settings className="mr-2 h-4 w-4" />
          Todos los ajustes
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            setTheme(isDark ? 'light' : 'dark');
          }}
        >
          {mounted && isDark ? (
            <Sun className="mr-2 h-4 w-4" />
          ) : (
            <Moon className="mr-2 h-4 w-4" />
          )}
          {mounted && isDark ? 'Tema claro' : 'Tema oscuro'}
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={signOut} className="text-destructive">
          <LogOut className="mr-2 h-4 w-4" />
          Cerrar sesión
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
